/**
 * AlertHistory Component
 *
 * Displays a scrollable list of alerts with level indicators,
 * resolve action, and auto-refresh support.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import { StatusBadge } from '../shared/StatusBadge';
import { llmAdminApi, type AlertItem } from '../../../services/llmAdminApi';

export interface AlertHistoryProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  token?: string;
  className?: string;
}

const levelVariantMap: Record<string, 'warning' | 'error' | 'info'> = {
  info: 'info',
  warning: 'warning',
  critical: 'error',
};

const levelLabelMap: Record<string, string> = {
  info: '信息',
  warning: '警告',
  critical: '严重',
};

const typeLabelMap: Record<string, string> = {
  quota_warning: '配额警告',
  quota_critical: '配额严重',
  health_check_failure: '健康检查失败',
  pool_unavailable: '池不可用',
  rate_limit_exceeded: '限流超限',
};

const formatTime = (ts: string): string => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

export const AlertHistory: React.FC<AlertHistoryProps> = ({
  autoRefresh = true,
  refreshInterval = 30000,
  token,
  className = '',
}) => {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const result = await llmAdminApi.getAlerts(token, showResolved, 200);
      setAlerts(result.alerts);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token, showResolved]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchAlerts, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchAlerts]);

  const handleResolve = useCallback(
    async (alertId: string) => {
      setResolving(alertId);
      try {
        await llmAdminApi.resolveAlert(token, alertId);
        await fetchAlerts();
      } catch {
        // silent
      } finally {
        setResolving(null);
      }
    },
    [token, fetchAlerts]
  );

  if (loading) {
    return (
      <div className={clsx('text-center py-6 text-gray-500 text-sm', className)}>
        <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
        加载告警历史...
      </div>
    );
  }

  return (
    <div className={clsx('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">
          告警历史 ({alerts.length})
        </h4>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
            显示已解决
          </label>
          <button
            type="button"
            onClick={fetchAlerts}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            刷新
          </button>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm border border-dashed border-gray-300 rounded-lg">
          暂无告警记录
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={clsx(
                'p-3 rounded-lg border text-sm',
                alert.resolved
                  ? 'bg-gray-50 border-gray-200 opacity-60'
                  : alert.level === 'critical'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-yellow-50 border-yellow-200'
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge
                      variant={levelVariantMap[alert.level] || 'neutral'}
                      size="sm"
                    >
                      {levelLabelMap[alert.level] || alert.level}
                    </StatusBadge>
                    <span className="text-xs text-gray-500">
                      {typeLabelMap[alert.type] || alert.type}
                    </span>
                    {alert.provider && (
                      <span className="text-xs text-gray-400">
                        {alert.provider}
                        {alert.pool_index !== null && alert.pool_index !== undefined && `#${alert.pool_index}`}
                      </span>
                    )}
                  </div>
                  <div className="font-medium text-gray-800">
                    {alert.title}
                  </div>
                  <div className="text-gray-600 mt-0.5 text-xs">
                    {alert.message}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {formatTime(alert.timestamp)}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {!alert.resolved && (
                    <button
                      type="button"
                      onClick={() => handleResolve(alert.id)}
                      disabled={resolving === alert.id}
                      className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {resolving === alert.id ? '处理中...' : '解决'}
                    </button>
                  )}
                  {alert.resolved && (
                    <span className="text-xs text-green-600">已解决</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertHistory;
